'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Film, Power, PowerOff, Send, Loader2, Square, RefreshCw,
  FolderOpen, ChevronDown, RotateCcw, Wifi, WifiOff,
} from 'lucide-react';
import { MarkdownBlock } from '@/components/chat/Markdown';
import { ToolBlock } from '@/components/chat/ToolBlock';
import { useSessionChat } from '@/components/chat/useSessionChat';
import type { ChatMessage, MessagePart } from '@/components/chat/types';

import { gateway, resolveApi } from '@/lib/api';

const GW = '/api/gw';
// Dedicated gateway session for the studio — same machinery as the Ads/SEO
// assistants (useSessionChat): module-level run manager, reattach after a
// reload, disk-backed history. Replaces the old hand-rolled SSE loop that
// kept messages in localStorage and lost in-flight runs on refresh.
const SLUG = 'resolve-studio';

// ─── Types ───

interface ResolveStatus {
  running: boolean;
  connected: boolean;
  current_project: string | null;
  current_timeline: string | null;
  projects_count: number;
}

interface Project {
  name: string;
  size_mb: number;
}

type ConnectionStatus = 'connected' | 'disconnected' | 'checking';

// ─── Resolve Context Builder ───

const SYSTEM_CONTEXT = `You are Beast, controlling DaVinci Resolve via Python scripts.

TOOLS AVAILABLE:
- resolve_god_mode.py at ~/beast/projects/tools/vsl-editor/shared-memory/analysis/ — direct DB access (works even when Resolve is closed)
- resolve-toolkit at ~/beast/projects/tools/vsl-editor/resolve-toolkit/ — 14-step automated pipeline
- Resolve Python scripting API (when Resolve is running) — runtime control
- All proven methods: blade cuts via AppleScript, Fusion keyframes via paste, subtitle pipeline, image overlays, etc.
- Reference docs at ~/beast/docs/resolve/

RESOLVE BRIDGE at http://127.0.0.1:3101:
- GET /status — check if Resolve is running
- POST /launch — open/focus Resolve
- POST /execute — run commands (open_project, set_timeline, blade, render_all, create_subtitles, save, page)
- GET /projects — list projects
- GET /projects/{name} — project detail with timelines
- GET /projects/{name}/timelines/{tl} — track/clip detail

When the user asks you to do something in Resolve:
1. Use bash to call the bridge API or run Python scripts directly
2. Show what you're doing and the results
3. If Resolve isn't running, launch it first via the bridge`;

function buildResolveContext(status: ResolveStatus | null, projects: Project[]): string {
  const parts = ['CURRENT RESOLVE STATE:'];
  if (status) {
    parts.push(`Running: ${status.running}`);
    parts.push(`Connected: ${status.connected}`);
    if (status.current_project) parts.push(`Current project: ${status.current_project}`);
    if (status.current_timeline) parts.push(`Current timeline: ${status.current_timeline}`);
  } else {
    parts.push('Status: unknown (bridge may be offline)');
  }
  if (projects.length > 0) {
    parts.push(`Available projects: ${projects.map(p => p.name).join(', ')}`);
  }
  return `${SYSTEM_CONTEXT}\n\n${parts.join('\n')}`;
}

// ─── Message Components ───

function AssistantParts({ parts }: { parts: MessagePart[] }) {
  return (
    <>
      {parts.map((p, i) =>
        p.type === 'text' ? (
          p.content && (
            <div key={i} className="rounded-2xl px-4 py-3 bg-white/[0.04] border border-[rgb(var(--border))]/40">
              <MarkdownBlock text={p.content} />
            </div>
          )
        ) : p.type === 'thinking' ? (
          <div key={i} className="text-xs text-gray-500 italic whitespace-pre-wrap px-1">{p.content}</div>
        ) : (
          <ToolBlock key={p.tool.id || i} name={p.tool.name} input={p.tool.input} output={p.tool.output} status={p.tool.status} />
        ),
      )}
    </>
  );
}

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const time = new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[75%]">
          <div className="rounded-2xl px-4 py-3 bg-orange-600/15 border border-orange-500/20">
            <p className="text-[13.5px] text-gray-200 whitespace-pre-wrap">{msg.content}</p>
          </div>
          <div className="text-[10px] text-orange-600/40 text-right mt-1">{time}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-xs">🎬</span>
          <span className="text-[10px] font-medium uppercase tracking-wider text-gray-600">Resolve</span>
          <span className="text-[10px] text-gray-700">{time}</span>
        </div>
        {msg.parts && msg.parts.length > 0 ? (
          <AssistantParts parts={msg.parts} />
        ) : (
          <>
            {msg.tools?.map(tool => (
              <ToolBlock key={tool.id} name={tool.name} input={tool.input} output={tool.output} status={tool.status} />
            ))}
            {msg.content && (
              <div className="rounded-2xl px-4 py-3 bg-white/[0.04] border border-[rgb(var(--border))]/40">
                <MarkdownBlock text={msg.content} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StreamingBubble({ text, parts, thinking }: { text: string; parts: MessagePart[]; thinking: boolean }) {
  const runningTool = parts.some(p => p.type === 'tool' && p.tool.status === 'running');
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-xs">🎬</span>
          <span className="text-[10px] font-medium uppercase tracking-wider text-gray-600">Resolve</span>
        </div>
        <AssistantParts parts={parts} />
        {thinking && !text && !runningTool && (
          <div className="rounded-2xl px-4 py-3 bg-white/[0.04] border border-[rgb(var(--border))]/40">
            <div className="flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 text-orange-400 animate-spin" />
              <span className="text-xs text-gray-500">Thinking…</span>
            </div>
          </div>
        )}
        {text && (
          <div className="rounded-2xl px-4 py-3 bg-white/[0.04] border border-[rgb(var(--border))]/40">
            <MarkdownBlock text={text} />
            <span className="inline-block w-1.5 h-4 bg-orange-400 animate-pulse rounded-sm ml-0.5 align-text-bottom" />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───

export default function ResolveStudio() {
  const [status, setStatus] = useState<ResolveStatus | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [input, setInput] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('checking');
  const [sessionReady, setSessionReady] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const projectsRef = useRef<HTMLDivElement>(null);

  const chat = useSessionChat(SLUG);

  // Open (or wake) the dedicated gateway session — same call the Ads/SEO
  // assistants use; cwd ~/beast so bash + the bridge scripts are in reach.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${GW}/sessions/open`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug: SLUG, path: '.', name: 'Resolve Studio', icon: '🎬' }),
        });
        const d = await r.json();
        if (!d.ok) throw new Error(d.error || 'Failed to open Resolve session');
        if (!cancelled) setSessionReady(true);
      } catch (e) {
        if (!cancelled) setOpenError(e instanceof Error ? e.message : 'Failed to open Resolve session');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Gateway health check (drives the wifi dot in the header)
  useEffect(() => {
    const check = () => {
      gateway.health()
        .then(res => setConnectionStatus(res.ok ? 'connected' : 'disconnected'))
        .catch(() => setConnectionStatus('disconnected'));
    };
    check();
    const interval = setInterval(check, 10_000);
    return () => clearInterval(interval);
  }, []);

  // ─── Data Fetching ───

  const fetchStatus = useCallback(async () => {
    try {
      const data = await resolveApi.list();
      if (data && !data.error) setStatus(data);
    } catch { /* bridge offline */ }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const data = await resolveApi.projects();
      if (data && !data.error) setProjects(data.projects || []);
    } catch { /* bridge offline */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchProjects();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchProjects]);

  // Auto-scroll on new content
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat.messages, chat.streamText, chat.streamParts]);

  // Close projects dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (projectsRef.current && !projectsRef.current.contains(e.target as Node)) {
        setProjectsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ─── Memoized Context ───

  const resolveContext = useMemo(
    () => buildResolveContext(status, projects),
    [status, projects],
  );

  // ─── Actions ───

  // Send with the Resolve grounding on the FIRST turn only — the gateway
  // session remembers it after that (classic assistant pattern).
  const submit = useCallback((text: string) => {
    const t = text.trim();
    if (!t || !sessionReady) return;
    const context = chat.messages.length === 0 ? resolveContext : undefined;
    chat.send(t, undefined, context);
  }, [sessionReady, chat, resolveContext]);

  const handleLaunch = async () => {
    setLaunching(true);
    try {
      await resolveApi.launch();
      setTimeout(fetchStatus, 3000);
    } catch { /* */ }
    finally { setLaunching(false); }
  };

  const handleOpenProject = (name: string) => {
    setProjectsOpen(false);
    const prompt = status?.running
      ? `Open project "${name}" in DaVinci Resolve`
      : `Launch DaVinci Resolve and open project "${name}"`;
    submit(prompt);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    if (text === '/clear') { chat.clear(); setInput(''); return; }
    submit(text);
    setInput('');
  };

  // Auto-resize textarea
  const autoResize = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, 200);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > 200 ? 'auto' : 'hidden';
  }, []);

  useEffect(() => { autoResize(); }, [input, autoResize]);

  // ─── Connection indicator ───

  const ConnectionDot = () => {
    if (connectionStatus === 'checking') return null;
    const connected = connectionStatus === 'connected';
    return (
      <div className="flex items-center gap-1.5" title={connected ? 'Gateway connected' : 'Gateway disconnected'}>
        {connected
          ? <Wifi className="w-3 h-3 text-green-500/60" />
          : <WifiOff className="w-3 h-3 text-red-500/60" />
        }
      </div>
    );
  };

  // ─── Render ───

  return (
    <div className="flex flex-col h-full bg-[rgb(var(--background))]">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-3 px-5 py-2 border-b border-[rgb(var(--border))]/60">
        {/* Status indicator */}
        <div className="flex items-center gap-2">
          <Film className="w-4 h-4 text-orange-400" />
          <div className={`w-2 h-2 rounded-full ${status?.running ? 'bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.5)]' : 'bg-gray-600'}`} />
          <span className="text-xs text-gray-500">
            {status?.running ? (status.connected ? 'Connected' : 'Running') : 'Offline'}
          </span>
        </div>

        {/* Current project badge */}
        {status?.current_project && (
          <span className="text-[11px] text-orange-300/70 bg-orange-500/10 px-2 py-0.5 rounded border border-orange-500/20">
            {status.current_project}
            {status.current_timeline && ` → ${status.current_timeline}`}
          </span>
        )}

        <div className="flex-1" />

        {/* Gateway connection */}
        <ConnectionDot />

        {/* Projects dropdown */}
        <div ref={projectsRef} className="relative">
          <button
            onClick={() => setProjectsOpen(!projectsOpen)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white hover:bg-white/5 border border-[rgb(var(--border))]/60 transition-colors"
          >
            <FolderOpen className="w-3.5 h-3.5" />
            <span>Projects</span>
            <span className="text-gray-600">{projects.length}</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${projectsOpen ? 'rotate-180' : ''}`} />
          </button>

          {projectsOpen && (
            <div className="absolute right-0 top-full mt-1 w-64 bg-gray-900 border border-[rgb(var(--border))]/60 rounded-lg shadow-xl z-50 max-h-[50vh] overflow-y-auto">
              {projects.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-gray-600">
                  {status ? 'No projects found' : 'Bridge offline — cannot list projects'}
                </div>
              ) : (
                projects.map(p => (
                  <button
                    key={p.name}
                    onClick={() => handleOpenProject(p.name)}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-[rgb(var(--muted))]/80 transition-colors border-b border-[rgb(var(--border))]/40 last:border-0"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Film className="w-3.5 h-3.5 text-orange-400/50 flex-shrink-0" />
                      <span className="text-sm text-gray-200 truncate">{p.name}</span>
                    </div>
                    <span className="text-[10px] text-gray-600 flex-shrink-0 ml-2">{p.size_mb}MB</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Launch/Focus */}
        <button
          onClick={handleLaunch}
          disabled={launching}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
            status?.running
              ? 'bg-green-500/10 text-green-400 border-green-500/30 hover:bg-green-500/20'
              : 'bg-orange-500/15 text-orange-400 border-orange-500/30 hover:bg-orange-500/25'
          }`}
        >
          {launching
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : status?.running ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />
          }
          {status?.running ? 'Focus' : 'Launch'}
        </button>

        {/* Refresh */}
        <button
          onClick={() => { fetchStatus(); fetchProjects(); }}
          className="p-1.5 rounded-md text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
          title="Refresh status & projects"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {openError && (
          <div className="flex justify-start">
            <div className="rounded-lg px-3 py-2 bg-red-500/10 border border-red-500/20 text-xs text-red-400">
              {openError}
            </div>
          </div>
        )}
        {chat.historyHydrated && chat.messages.length === 0 && !chat.streaming && (
          <div className="flex flex-col items-center justify-center h-full text-gray-600 gap-3">
            <span className="text-4xl">🎬</span>
            <div className="text-sm">DaVinci Resolve Command Center</div>
            <div className="text-xs text-gray-700">
              {status?.running
                ? `Connected to ${status.current_project || 'Resolve'}`
                : 'Launch Resolve or ask me anything about your projects'
              }
            </div>
            <div className="text-[11px] text-gray-800 mt-2">/clear to reset chat</div>
          </div>
        )}

        {chat.messages.map(msg => (
          <MessageBubble key={msg.id} msg={msg} />
        ))}

        {chat.streaming && (
          <StreamingBubble text={chat.streamText} parts={chat.streamParts} thinking={chat.thinking} />
        )}

        {chat.error && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-lg px-3 py-2 bg-red-500/10 border border-red-500/20">
              <span className="text-xs text-red-400">{chat.error}</span>
              {chat.canRetry && (
                <button
                  onClick={chat.retry}
                  className="flex items-center gap-1 text-[10px] text-red-300 hover:text-red-200 transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  Retry
                </button>
              )}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 border-t border-[rgb(var(--border))]/60 bg-white/[0.05] px-4 py-3">
        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
            placeholder={!sessionReady ? 'Connecting to Resolve session...' : status?.running ? 'Tell Resolve what to do...' : 'Ask about your video projects...'}
            rows={1}
            className="flex-1 bg-[rgb(var(--muted))]/40 border border-[rgb(var(--border))]/40 rounded-xl px-4 py-2.5 text-sm text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-orange-500/40 focus:ring-1 focus:ring-orange-500/20 resize-none min-h-[40px] max-h-[200px]"
            style={{ overflowY: 'hidden' }}
          />
          {chat.streaming ? (
            <button type="button" onClick={chat.abort} className="p-2.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-colors flex-shrink-0">
              <Square className="w-4 h-4" />
            </button>
          ) : (
            <button type="submit" disabled={!input.trim() || !sessionReady} className="p-2.5 rounded-xl bg-orange-500/20 border border-orange-500/30 text-orange-400 hover:bg-orange-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0">
              <Send className="w-4 h-4" />
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
